import type {
  ActionId,
  IProposal,
  ProposalId,
} from '@shared/models/proposals.model';
import { isOneOf } from '@shared/helpers/array.helpers';
import { KeyboardKeys } from '@shared/models/keyboard.model';

export type ListNavigationDirection = 'next' | 'previous';

const SUPPORTED_KEYBOARD_NAVIGATION_KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'e',
  'E',
  'p',
  'P',
  's',
  'S',
  'Enter',
  'Escape',
  KeyboardKeys.BACKSPACE,
  KeyboardKeys.DELETE,
]);

type ProposalSelection = Pick<IProposal, 'id'>;

type ActionListNavigationOptions = {
  actionIds: ActionId[];
  selectedId?: string;
  direction: ListNavigationDirection;
};

/**
 * Returns whether a keydown should be handled by element-list keyboard navigation.
 * Some browsers emit keydown with an undefined `key` (e.g. while typing in inputs).
 */
export const isSupportedKeyboardNavigationKey = (
  key: string | undefined,
): boolean => {
  if (!key) return false;
  if (key.length === 1 && key >= '0' && key <= '9') return true;
  return SUPPORTED_KEYBOARD_NAVIGATION_KEYS.has(key);
};

/**
 * Converts supported vertical arrow keys into a {@link ListNavigationDirection}.
 */
export const getVerticalArrowNavigationDirection = (
  key: string,
): ListNavigationDirection | undefined => {
  if (key === 'ArrowDown') return 'next';
  if (key === 'ArrowUp') return 'previous';
  return undefined;
};

/**
 * Returns the next selected proposal id when cycling through baseline + proposals.
 * Baseline is represented by `undefined`.
 */
export const getNextProposalSelectionId = (
  proposals: ProposalSelection[],
  selectedProposalId: ProposalId | undefined,
  direction: ListNavigationDirection,
): ProposalId | undefined => {
  const ids: (ProposalId | undefined)[] = [
    undefined,
    ...proposals.map((p) => p.id),
  ];
  const currentIndex = ids.indexOf(selectedProposalId);
  const baseIndex = currentIndex === -1 ? 0 : currentIndex;
  const offset = direction === 'previous' ? -1 : 1;
  const nextIndex = (baseIndex + offset + ids.length) % ids.length;
  return ids[nextIndex];
};

type VisibleListNavigationOptions = {
  visibleIds: string[];
  selectedId?: string;
  direction: ListNavigationDirection;
  /** When omitted, membership in {@link visibleIds} decides if selection counts. */
  isKnownSelection?: (id: string) => boolean;
};

/**
 * Returns the next row id when moving up/down a flat visible list (imports,
 * actions, and other tabs that share the same arrow semantics).
 */
export const getNextVisibleListRowId = ({
  visibleIds,
  selectedId,
  direction,
  isKnownSelection,
}: VisibleListNavigationOptions): string | undefined => {
  if (visibleIds.length === 0) return undefined;

  const isSelected =
    isKnownSelection ?? ((id: string) => visibleIds.includes(id));

  const resolvedSelectedId =
    selectedId && isSelected(selectedId) ? selectedId : undefined;
  if (!resolvedSelectedId) {
    return direction === 'next' ? visibleIds[0] : undefined;
  }

  const currentIndex = visibleIds.indexOf(resolvedSelectedId);
  if (currentIndex === -1) {
    return direction === 'next' ? visibleIds[0] : undefined;
  }

  const nextIndex =
    direction === 'next'
      ? Math.min(currentIndex + 1, visibleIds.length - 1)
      : Math.max(currentIndex - 1, 0);

  if (nextIndex === currentIndex) return undefined;
  return visibleIds[nextIndex];
};

/**
 * Returns the next action row id for actions-tab arrow navigation.
 * A selected id from another tab is treated as no action selection.
 */
export const getNextActionListRowId = ({
  actionIds,
  selectedId,
  direction,
}: ActionListNavigationOptions): ActionId | undefined =>
  getNextVisibleListRowId({
    visibleIds: actionIds,
    selectedId,
    direction,
    isKnownSelection: (id) => isOneOf(actionIds, id),
  });
